import React, { useState, useEffect } from "react";
import { X, ZoomIn, Mountain } from "lucide-react";
import { contentService } from "../../services/contentService";
import { imgUrl } from "../../utils/imgUrl";

const FALLBACK = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    category: "views",
    title: "Mountain View",
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800&h=600&fit=crop",
    category: "rooms",
    title: "Deluxe Room",
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&h=600&fit=crop",
    category: "views",
    title: "Sunset View",
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&h=600&fit=crop",
    category: "rooms",
    title: "Family Cottage",
  },
  {
    id: 5,
    url: "https://images.unsplash.com/photo-1464207687429-7505649dae38?w=800&h=600&fit=crop",
    category: "nature",
    title: "Pine Forest",
  },
  {
    id: 6,
    url: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&h=600&fit=crop",
    category: "rooms",
    title: "Premium Cottage",
  },
  {
    id: 7,
    url: "https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=800&h=600&fit=crop",
    category: "nature",
    title: "Mountain Trail",
  },
  {
    id: 8,
    url: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=600&fit=crop",
    category: "restaurant",
    title: "Dining Area",
  },
  {
    id: 9,
    url: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop",
    category: "rooms",
    title: "Mountain Cottage",
  },
  {
    id: 10,
    url: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&h=600&fit=crop",
    category: "rooms",
    title: "Honeymoon Cottage",
  },
  {
    id: 11,
    url: "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=800&h=600&fit=crop",
    category: "views",
    title: "Valley View",
  },
  {
    id: 12,
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    category: "nature",
    title: "Himalayan Peak",
  },
];

const CATEGORIES = [
  { id: "all", name: "All Photos" },
  { id: "rooms", name: "Rooms" },
  { id: "views", name: "Views" },
  // { id: "nature", name: "Nature" },
  { id: "facilities", name: "Facilities" },
  // { id: "general", name: "General" },
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [images, setImages] = useState(FALLBACK);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    contentService
      .getGallery()
      .then((res) => {
        if (res.data.images?.length > 0) setImages(res.data.images);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered =
    selectedCategory === "all"
      ? images
      : images.filter((img) => img.category === selectedCategory);

  // Styles
  const sectionStyle = { padding: "64px 24px" };
  const containerStyle = { maxWidth: 1200, margin: "0 auto" };
  const btnFilterStyle = (active) => ({
    padding: "8px 24px",
    borderRadius: 9999,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    border: active ? "none" : "1.5px solid #e5e7eb",
    backgroundColor: active ? "#16a34a" : "transparent",
    color: active ? "#fff" : "#374151",
    boxShadow: active ? "0 2px 8px rgba(22,163,74,0.3)" : "none",
    transition: "all 0.2s",
  });

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          height: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=800&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(22,163,74,0.8) 0%, rgba(21,128,61,0.9) 100%)",
          }}
        ></div>
        <div
          style={{
            position: "relative",
            zIndex: 10,
            textAlign: "center",
            color: "#fff",
            padding: "0 24px",
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <p
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 6,
              fontWeight: "bolder",
              color: "rgba(255,255,255,0.7)",
              marginBottom: 12,
            }}
          >
            Visual Journey
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(40px, 6vw, 56px)",
              fontWeight: 900,
              marginBottom: 16,
            }}
          >
            Gallery
          </h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 17 }}>
            Explore the beauty of Burans Hikes through our photo collection
          </p>
        </div>
      </section>

      {/* Filter */}
      <section
        style={{
          padding: "16px 24px",
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          position: "sticky",
          top: 64,
          zIndex: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            ...containerStyle,
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedCategory(c.id)}
              style={btnFilterStyle(selectedCategory === c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 80, color: "#9ca3af" }}>
              <Mountain
                size={48}
                style={{ margin: "0 auto 16px", opacity: 0.3 }}
              />
              <p style={{ fontSize: 16 }}>No photos in this category.</p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 20,
              }}
            >
              {filtered.map((image) => {
                const hovered = hoveredId === image.id;
                return (
                  <div
                    key={image.id}
                    onClick={() => setSelectedImage(image)}
                    onMouseEnter={() => setHoveredId(image.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      position: "relative",
                      height: 256,
                      borderRadius: 20,
                      overflow: "hidden",
                      cursor: "pointer",
                      boxShadow: hovered
                        ? "0 12px 40px rgba(0,0,0,0.15)"
                        : "0 4px 20px rgba(0,0,0,0.08)",
                      transform: hovered ? "translateY(-4px)" : "translateY(0)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                    }}
                  >
                    <img
                      src={imgUrl(image.url || image.src)}
                      alt={image.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(to top, rgba(22,163,74,0.8) 0%, transparent 100%)",
                        opacity: hovered ? 1 : 0,
                        transition: "opacity 0.3s",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <ZoomIn size={28} color="#fff" />
                      <p
                        style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
                      >
                        {image.title}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.95)",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              padding: 10,
              border: "none",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: "50%",
              cursor: "pointer",
              color: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            <X size={24} />
          </button>
          <img
            src={imgUrl(selectedImage.url || selectedImage.src)}
            alt={selectedImage.title}
            style={{
              maxWidth: "90%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 20,
              boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <p
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: 18,
              fontWeight: 600,
              marginTop: 24,
            }}
          >
            {selectedImage.title}
          </p>
        </div>
      )}
    </div>
  );
};

export default Gallery;
